import AnchorButton from 'components/AnchorButton'
import { useGlobalState } from 'context/GlobalStateContext'
import { useEffect, useState } from 'react'
import { CASStatus, Report } from 'shared'
import { useSingleReportsContext } from '../../SingleReportsContext'
import {
  useReportCasPacketInfoQuery,
  useReportConcreteTestSetPacketInfoQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useReportRecordSelectContext } from 'components/CIMSSelect/common/ReportRecordSelect/ReportRecordSelectContext'
import { useAnyTrue } from 'hooks/useAnyTrue'
import { useReportURLDependencies } from 'hooks/useReportURLDependencies'

const GenerateSingleButton: React.FC = () => {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const { loading } = useReportRecordSelectContext()
  const { globalId, report } = useSingleReportsContext()
  const [href, setHref] = useState<string | null | undefined>()
  const dependencies = useReportURLDependencies()
  const [downloadUrl, setDownloadUrl] = useState<string | null | undefined>(
    undefined
  )
  const { data: concreteTestSetInfo, loading: concreteTestSetLoading } =
    useReportConcreteTestSetPacketInfoQuery({
      ...DefaultQueryHookOptions,
      skip: !projectGlobalId,
      variables: {
        projectId: projectGlobalId!,
        concreteTestSetFieldInput: {
          deleted: false,
          orderBy: [{ field: 'testSetId' }],
        },
      },
    })
  const { data: submittalInfo, loading: submittalLoading } =
    useReportCasPacketInfoQuery({
      ...DefaultQueryHookOptions,
      variables: {
        input: {
          projectId: { equals: projectGlobalId },
          statusId: { equals: CASStatus.Closed.id },
          deleted: false,
        },
      },
    })
  const [isLoading] = useAnyTrue([
    concreteTestSetLoading,
    submittalLoading,
    loading,
  ])
  const [isDisabled] = useAnyTrue([!report, !globalId])
  useEffect(() => {
    if (
      report === Report.ConcreteTestSet &&
      !!globalId &&
      !!concreteTestSetInfo?.project?.concreteTestSets
    )
      setDownloadUrl(
        concreteTestSetInfo.project.concreteTestSets.find(
          ({ globalId: id }) => globalId === id
        )?.concreteTestSetMetadata?.packetSharePointResource?.downloadUrl
      )
    else if (
      report === Report.ConstructionAdminSubmittal &&
      !!globalId &&
      submittalInfo?.constructionAdminSubmittals?.records
    ) {
      const submittal = submittalInfo.constructionAdminSubmittals.records.find(
        ({ id }) => id.toString() === globalId.toString()
      )
      setDownloadUrl(submittal?.packetSharePointResource?.downloadUrl)
    } else {
      setDownloadUrl(undefined)
    }
  }, [
    concreteTestSetInfo?.project?.concreteTestSets,
    submittalInfo?.constructionAdminSubmittals?.records,
    globalId,
    report,
  ])

  useEffect(() => {
    if (!report) return
    if (!globalId) return
    if (report.queryParameter === 'packet')
      setHref(
        downloadUrl ??
          report.toURL({
            ...dependencies,
            globalId,
            skipSharePointUpload: true,
          })
      )
    else if (report.queryParameter === 'construction-admin-submittal')
      setHref(downloadUrl)
    else setHref(report.toURL({ ...dependencies, globalId }))
  }, [downloadUrl, globalId, report, projectGlobalId, dependencies])

  return (
    <AnchorButton
      href={href}
      type="primary"
      loading={isLoading}
      disabled={isLoading || isDisabled || !href}
    >
      {report ? `Get ${report.name}` : 'Select a Report'}
    </AnchorButton>
  )
}

export default GenerateSingleButton
